import {
  CreatedInvitation,
  Invitation,
  InvitationPreview,
  Me,
  OrgUser,
  PasskeyOptions,
  ProjectMember,
  StepUpState,
  UserDetail,
  type AcceptInvitationRequest,
  type CreateInvitationRequest,
  type MemberAccess,
  type ProjectAccess,
  type ProjectGrant,
  type Role,
} from "@cloudserver/contracts";
import { startAuthentication } from "@simplewebauthn/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { api } from "./client.js";
import { keys, useAuthState, useProject, useProjects } from "./queries.js";

/*
 * Governance (docs/users.md): the users section, invitations, project access
 * and step-up. The API decides; these hooks only show what it allows.
 */

export const userKeys = {
  users: ["users"] as const,
  user: (id: string) => ["users", id] as const,
  invitations: ["invitations"] as const,
  members: (projectId: string) => ["projects", projectId, "members"] as const,
};

/** The signed-in person (always present inside the router). */
export function useMe(): Me | null {
  return useAuthState().data?.me ?? null;
}

export const useIsAdmin = (): boolean => useMe()?.role === "admin";

/** The caller's access to a project, from the project list; undefined while loading. */
export function useProjectAccess(projectId: string | undefined): ProjectAccess | undefined {
  const projects = useProjects(projectId !== undefined);
  return projects.data?.find((p) => p.id === projectId)?.access;
}

/** Whether the caller may change things in the project (owners and administrators). */
export function useCanWrite(projectId: string): boolean {
  const access = useProjectAccess(projectId);
  return access === "owner" || access === "admin";
}

/** Whether the caller may change the project, and if not, why (in the person's language). */
export interface ReadOnlyState {
  /** Changes are refused: the token is read-only, or the caller is a guest. */
  readOnly: boolean;
  /** The precise reason (`guest_read_only` or `read_only_token`); empty when changes are allowed. */
  reason: string;
  /**
   * The token is the cause: pages say so in a notice. Guests already get the
   * shell's notice that the project is view-only.
   */
  token: boolean;
}

/**
 * The one answer pages use to disable a write control and explain why:
 * guests may only look (docs/users.md) and a read-only token cannot change
 * anything. The API refuses both anyway; the panel says so first.
 */
export function useReadOnly(projectId: string): ReadOnlyState {
  const { t } = useTranslation();
  const project = useProject(projectId);
  const access = useProjectAccess(projectId);
  if (access === "guest")
    return { readOnly: true, reason: t("errors.reasons.guest_read_only"), token: false };
  if (project.data?.connection?.permission === "read")
    return { readOnly: true, reason: t("errors.reasons.read_only_token"), token: true };
  return { readOnly: false, reason: "", token: false };
}

/** Governance writes refresh people, invitations, project access and the project list. */
function useGovernanceMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: userKeys.users });
      await client.invalidateQueries({ queryKey: userKeys.invitations });
      await client.invalidateQueries({ queryKey: keys.projects });
    },
  });
}

export const useUsers = () =>
  useQuery({
    queryKey: userKeys.users,
    queryFn: () => api("/users", { schema: z.array(OrgUser) }),
  });

export const useUser = (id: string) =>
  useQuery({
    queryKey: userKeys.user(id),
    queryFn: () => api(`/users/${id}`, { schema: UserDetail }),
  });

export const useInvitations = () =>
  useQuery({
    queryKey: userKeys.invitations,
    queryFn: () => api("/invitations", { schema: z.array(Invitation) }),
  });

export const useCreateInvitation = () =>
  useGovernanceMutation((body: CreateInvitationRequest) =>
    api("/invitations", { method: "POST", body, schema: CreatedInvitation }),
  );

export const useRevokeInvitation = () =>
  useGovernanceMutation((id: string) => api(`/invitations/${id}`, { method: "DELETE" }));

export const useChangeRole = (id: string) =>
  useGovernanceMutation((role: Role) =>
    api(`/users/${id}`, { method: "PATCH", body: { role }, schema: OrgUser }),
  );

export const useSetUserProjects = (id: string) =>
  useGovernanceMutation((projects: ProjectGrant[]) =>
    api(`/users/${id}/projects`, { method: "PUT", body: { projects }, schema: OrgUser }),
  );

export const useSuspendUser = (id: string) =>
  useGovernanceMutation((suspend: boolean) =>
    api(`/users/${id}/${suspend ? "suspend" : "reactivate"}`, { method: "POST", schema: OrgUser }),
  );

/** Refreshes only the list: the removed person's own page would answer 404. */
export function useRemoveUser(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/users/${id}`, { method: "DELETE" }),
    onSuccess: () => client.invalidateQueries({ queryKey: userKeys.users, exact: true }),
  });
}

export const useResetSecondFactor = (id: string) =>
  useGovernanceMutation(() =>
    api(`/users/${id}/reset-second-factor`, { method: "POST", schema: OrgUser }),
  );

export const useProjectMembers = (projectId: string) =>
  useQuery({
    queryKey: userKeys.members(projectId),
    queryFn: () => api(`/projects/${projectId}/members`, { schema: z.array(ProjectMember) }),
  });

export const useSetProjectMember = (projectId: string) =>
  useGovernanceMutation(({ userId, access }: { userId: string; access: MemberAccess }) =>
    api(`/projects/${projectId}/members/${userId}`, {
      method: "PUT",
      body: { access },
      schema: OrgUser,
    }),
  );

export const useRemoveProjectMember = (projectId: string) =>
  useGovernanceMutation((userId: string) =>
    api(`/projects/${projectId}/members/${userId}`, { method: "DELETE" }),
  );

/** A fresh second-factor check: an authenticator code, or the person's passkey. */
export const useStepUp = () =>
  useMutation({
    mutationFn: async (input: { code: string } | { passkey: true }) => {
      if ("code" in input)
        return api("/me/step-up", { method: "POST", body: input, schema: StepUpState });
      const { challengeId, options } = await api("/me/step-up/passkey/options", {
        method: "POST",
        schema: PasskeyOptions,
      });
      const response = await startAuthentication({ optionsJSON: options as never });
      return api("/me/step-up", {
        method: "POST",
        body: { challengeId, response },
        schema: StepUpState,
      });
    },
  });

export const useInvitationPreview = (token: string) =>
  useQuery({
    queryKey: ["invitation", token],
    queryFn: () =>
      api("/invitations/preview", { method: "POST", body: { token }, schema: InvitationPreview }),
    enabled: token !== "",
    retry: false,
  });

export function useAcceptInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptInvitationRequest) =>
      api("/invitations/accept", { method: "POST", body, schema: Me }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.auth }),
  });
}
